import React from 'react';
import {
  formatUKMobileAsYouType,
  isValidUKMobile,
} from '../lib/phone';

/**
 * PhoneInput — the ONLY phone field used anywhere in the app.
 *
 * Behaviour:
 *   • Auto-formats to canonical UK mobile `XXXXX XXXXXX` as the user
 *     types. Digits-only; anything else is filtered.
 *   • Live validation: red-border + inline error message once the
 *     user leaves the field with an invalid entry.
 *   • "Non-UK number" checkbox next to the field. When ticked:
 *       – The field becomes OPTIONAL (empty is OK).
 *       – No format enforcement (the raw string is passed to onChange).
 *       – The customer is flagged `is_non_uk_number=true` upstream so
 *         the SMS service skips them.
 *
 * Controlled component. Emits:
 *   onChange({ value, isNonUK, isValid })
 *
 * where `value` is either the canonical UK format or (if isNonUK) the
 * raw text.
 */

export const PhoneInput = ({
  value = '',
  isNonUK = false,
  onChange,
  required = true,
  label = 'Phone',
  placeholder = '07700 900123',
  testIdPrefix = 'phone',
  disabled = false,
  hideNonUKToggle = false,
}) => {
  const [touched, setTouched] = React.useState(false);

  // ---- Change handlers ----
  const handleInput = (e) => {
    const raw = e.target.value;
    if (isNonUK) {
      // Non-UK: pass through raw, don't touch it. Field is optional
      // when non-UK, so an empty value is always valid too.
      onChange?.({ value: raw, isNonUK: true, isValid: true });
      return;
    }
    const formatted = formatUKMobileAsYouType(raw);
    onChange?.({
      value: formatted,
      isNonUK: false,
      isValid: isValidUKMobile(formatted) || (!required && formatted.length === 0),
    });
  };

  const handleToggleNonUK = (e) => {
    const nowNonUK = e.target.checked;
    // Clear the field when the mode changes so a half-typed value
    // doesn't fail validation in the new mode.
    onChange?.({
      value: '',
      isNonUK: nowNonUK,
      isValid: nowNonUK ? true : !required, // non-UK: optional, so valid empty
    });
    setTouched(false);
  };

  // ---- Presentation ----
  const emptyOK = isNonUK || !required;
  const isEmpty = !value || value.length === 0;
  const looksValid = isNonUK ? true : isValidUKMobile(value);
  const showError = touched && !isEmpty && !looksValid;
  const showRequiredError = touched && required && isEmpty && !isNonUK;

  return (
    <div className="phone-input">
      {label && <label className="phone-input-label">{label}</label>}
      <div className="phone-input-row">
        <input
          type={isNonUK ? 'text' : 'tel'}
          inputMode={isNonUK ? 'text' : 'tel'}
          autoComplete="tel"
          className={`inp ${showError || showRequiredError ? 'inp-error' : ''}`}
          value={value}
          onChange={handleInput}
          onBlur={() => setTouched(true)}
          placeholder={isNonUK ? 'Enter number (any format)' : placeholder}
          maxLength={isNonUK ? 24 : 18} /* Raw allowance for +447..., "07 700 900123" spaces etc — the formatter enforces the actual 11-digit UK limit. */
          disabled={disabled}
          data-testid={`${testIdPrefix}-input`}
        />
      </div>
      <label className="phone-input-nonuk" style={hideNonUKToggle ? { display: 'none' } : undefined}>
        <input
          type="checkbox"
          checked={isNonUK}
          onChange={handleToggleNonUK}
          disabled={disabled}
          data-testid={`${testIdPrefix}-nonuk`}
        />
        Non-UK number
        <span className="phone-input-nonuk-hint">
          {isNonUK ? '· optional · SMS will not be sent' : ''}
        </span>
      </label>
      {/* Format error surfaces on ALL surfaces (previously it was
          suppressed when the non-UK toggle was hidden, which hid the
          only useful feedback on the Diary New Booking modal). */}
      {showError && (
        <div className="phone-input-error" data-testid={`${testIdPrefix}-error`}>
          Enter a UK mobile as 07xxx xxxxxx
        </div>
      )}
      {showRequiredError && (
        <div className="phone-input-error" data-testid={`${testIdPrefix}-required`}>
          Phone is required.
        </div>
      )}
    </div>
  );
};

export default PhoneInput;
